import React, { useEffect, useState } from "react"
import { UserPlus, Check, Loader2, X } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "src/components/ui/dropdown-menu"
import { fetchMembers } from "../api/projectsApi"
import { avatarTint } from "../constants"

function initials(name) {
  if (!name) return "?"
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase() || "?"
}

export function Avatar({ name, size = 24 }) {
  const tint = avatarTint(name || "")
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-[11px] font-medium ${tint}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  )
}

// Inline assignee control. Renders the current assignee (or an "Assign" affordance)
// and opens a member picker. Calls onAssign(membershipId | null).
export function AssigneePicker({ assignedTo, onAssign, isPending = false, compact = false }) {
  const [members, setMembers] = useState([])
  const [loaded, setLoaded] = useState(false)

  function loadMembers() {
    if (loaded) return
    fetchMembers()
      .then((d) => setMembers(d.memberships || []))
      .catch(() => setMembers([]))
      .finally(() => setLoaded(true))
  }

  // The caller may only know the membership_id (e.g. just-picked in a form);
  // resolve a display name from the loaded member list when needed.
  const resolvedName =
    assignedTo?.name ||
    members.find((m) => m.id === assignedTo?.membership_id)?.user?.name ||
    members.find((m) => m.id === assignedTo?.membership_id)?.user?.email ||
    "Member"

  return (
    <DropdownMenu onOpenChange={(open) => open && loadMembers()}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-2 rounded-md px-1.5 py-1 text-[13px] text-slate-700 transition-colors hover:bg-slate-50"
        >
          {isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-400" />
          ) : assignedTo ? (
            <>
              <Avatar name={resolvedName} />
              {!compact && <span className="truncate">{resolvedName}</span>}
            </>
          ) : (
            <span className="flex items-center gap-1.5 text-slate-400">
              <UserPlus className="h-3.5 w-3.5" />
              {!compact && "Assign"}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
        {assignedTo && (
          <>
            <DropdownMenuItem className="text-slate-500" onSelect={() => onAssign(null)}>
              <X className="mr-2 h-3.5 w-3.5" />
              Unassign
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        {!loaded ? (
          <div className="flex items-center gap-2 px-2 py-3 text-[13px] text-slate-400">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading members…
          </div>
        ) : members.length === 0 ? (
          <div className="px-2 py-3 text-[13px] text-slate-400">No members found</div>
        ) : (
          members.map((m) => {
            const selected = assignedTo?.membership_id === m.id
            return (
              <DropdownMenuItem key={m.id} onSelect={() => onAssign(m.id)} className="gap-2">
                <Avatar name={m.user?.name || m.user?.email} />
                <span className="min-w-0 flex-1 truncate">{m.user?.name || m.user?.email}</span>
                {selected && <Check className="h-3.5 w-3.5 text-slate-600" />}
              </DropdownMenuItem>
            )
          })
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
